/* ============================================================================
   Cursos — listagem com busca, ordenação, paginação e ações.
   ============================================================================ */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../components/AdminLayout';
import PageHeader from '../components/PageHeader';
import { Loading, PlanBadge } from '../components/ui';
import { confirm } from '../components/Modal';
import { toast } from '../components/Toast';
import { IconPlus, IconSearch, IconX, IconEdit, IconTrash, IconVideo, IconCourses } from '../components/icons';
import './Courses.css';

const PAGE_SIZE = 10;

export default function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [coursesRes, plansRes] = await Promise.all([
        api.get('/admin/courses', true),
        api.get('/admin/plans', true),
      ]);
      const plansData = plansRes.success ? (plansRes.data || []) : [];
      setPlans(plansData);
      const planMap = Object.fromEntries(plansData.map((p) => [p.id, p]));

      if (coursesRes.success) {
        setCourses((coursesRes.data || []).map((c) => {
          const id = c.id || c.PK?.replace('COURSE#', '');
          const planIds = c.planIds || [];
          const planName = planIds.length ? planIds.map((pid) => planMap[pid]?.name || pid).join(', ') : 'Gratuito';
          const planColor = planIds.length ? planMap[planIds[0]]?.color || '#d17d34' : null;
          return { ...c, id, title: c.title || c.name, planIds, planName, planColor };
        }));
      }
    } catch (err) {
      toast.error('Erro ao carregar cursos.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (course) => {
    const ok = await confirm({
      title: 'Deletar curso',
      message: `Tem certeza que deseja deletar "${course.title}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Deletar',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/courses/${course.id}`, true);
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
      toast.success('Curso deletado.');
    } catch (err) {
      toast.error(err.message || 'Erro ao deletar curso.');
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter((c) =>
      [c.title, c.category, c.duration, c.planName].some((v) => v?.toLowerCase().includes(q))
    );
  }, [courses, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = (a[sortKey] || '').toString().toLowerCase();
      const vb = (b[sortKey] || '').toString().toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const freeCount = courses.filter((c) => !c.planIds?.length).length;

  return (
    <AdminLayout>
      <PageHeader
        title="Cursos"
        subtitle="Crie, edite e organize os cursos da plataforma"
        action={
          <button className="mg-btn mg-btn-primary" onClick={() => navigate('/treinamentos/manager/courses/new')}>
            <IconPlus /> Novo curso
          </button>
        }
      />

      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="mg-flex mg-gap mg-wrap" style={{ marginBottom: 24 }}>
            <div className="mg-chip"><span className="mg-chip-value">{courses.length}</span><span className="mg-chip-label">Total</span></div>
            <div className="mg-chip"><span className="mg-chip-value" style={{ color: 'var(--mg-green-l)' }}>{freeCount}</span><span className="mg-chip-label">Gratuitos</span></div>
            {plans.map((plan) => (
              <div className="mg-chip" key={plan.id}>
                <span className="mg-chip-value" style={{ color: plan.color || 'var(--mg-coffee-gold)' }}>
                  {courses.filter((c) => c.planIds?.includes(plan.id)).length}
                </span>
                <span className="mg-chip-label">{plan.name}</span>
              </div>
            ))}
          </div>

          <div className="mg-toolbar">
            <div className="mg-search">
              <IconSearch />
              <input
                type="text"
                placeholder="Buscar por curso, categoria ou plano…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              {search && <button className="mg-search-clear" onClick={() => setSearch('')}><IconX /></button>}
            </div>
            <span className="mg-toolbar-count">{filtered.length} {filtered.length === 1 ? 'curso' : 'cursos'}</span>
          </div>

          <div className="mg-table-wrap">
            <table className="mg-table">
              <thead>
                <tr>
                  <SortTh label="Curso" field="title" {...{ sortKey, sortDir, handleSort }} />
                  <SortTh label="Categoria" field="category" {...{ sortKey, sortDir, handleSort }} />
                  <SortTh label="Duração" field="duration" {...{ sortKey, sortDir, handleSort }} />
                  <SortTh label="Plano" field="planName" {...{ sortKey, sortDir, handleSort }} />
                  <th className="mg-th-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="courses-empty">
                        <IconCourses size={30} />
                        <p>Nenhum curso encontrado</p>
                      </div>
                    </td>
                  </tr>
                ) : paged.map((course) => (
                  <tr key={course.id}>
                    <td>
                      <span className="mg-td-strong">{course.title}</span>
                      {course.folderName && <span className="mg-td-mono" style={{ display: 'block', marginTop: 3 }}>{course.folderName}</span>}
                    </td>
                    <td>{course.category || '—'}</td>
                    <td>{course.duration || '—'}</td>
                    <td><PlanBadge free={!course.planIds?.length} label={course.planName} color={course.planColor} /></td>
                    <td className="mg-td-actions">
                      <button className="mg-act mg-act-blue" onClick={() => navigate(`/treinamentos/manager/courses/${course.id}/videos`)} title="Gerenciar vídeos">
                        <IconVideo /> <span className="courses-act-lbl">Vídeos</span>
                      </button>
                      <button className="mg-act mg-act-amber" onClick={() => navigate(`/treinamentos/manager/courses/${course.id}/edit`)} title="Editar">
                        <IconEdit /> <span className="courses-act-lbl">Editar</span>
                      </button>
                      <button className="mg-act mg-act-red" onClick={() => handleDelete(course)} title="Deletar">
                        <IconTrash /> <span className="courses-act-lbl">Deletar</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </AdminLayout>
  );
}

function SortTh({ label, field, sortKey, sortDir, handleSort }) {
  const active = sortKey === field;
  return (
    <th className={`mg-th-sort${active ? ' is-active' : ''}`} onClick={() => handleSort(field)}>
      {label}
      <svg viewBox="0 0 10 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        {(!active || sortDir === 'asc') && <polyline points="2 5 5 2 8 5" />}
        {(!active || sortDir === 'desc') && <polyline points="2 9 5 12 8 9" />}
      </svg>
    </th>
  );
}

export function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);
  return (
    <div className="mg-pagination">
      <button className="mg-page-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
      {pages.map((p, i) => p === '…'
        ? <span key={`e${i}`} className="mg-page-dots">…</span>
        : <button key={p} className={`mg-page-btn${page === p ? ' is-active' : ''}`} onClick={() => onChange(p)}>{p}</button>
      )}
      <button className="mg-page-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}
